/**
 * CosmeticAdminSection — Innstillinger → Administrasjon → Butikkartikler.
 *
 * Admin surface for the avatar-shop catalogue (Wave 2 / v2.0a):
 *   - lists every active cosmetic item
 *   - "Ny artikkel" opens the inline form in create mode → POST /api/cosmetics
 *   - each row → Rediger (PATCH /:id) / Arkiver (DELETE /:id)
 *
 * Mirrors ChoreTemplateAdminSection's admin-scope recovery: every mutation runs
 * through `runGuarded`; a 403 admin-scope error opens <AdminReauthModal> and
 * retries the original action after a fresh re-auth so the admin never loses
 * their place. A list load that fails the same way shows <AdminScopeLocked>
 * instead of a dead retry.
 *
 * The form is kept inline (rather than a separate component) — the cosmetic
 * shape is small (kind / name / asset_key / point_cost / min_age / desc) and
 * v1 ships placeholder layers, so a dedicated <Form> with previews would be
 * premature until the real-art design drop lands.
 */
import { useId, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { COSMETIC_KINDS, type CosmeticKind } from '@stell/shared';
import { Button, LoadingState, ErrorState } from '../../components/primitives';
import { AdminReauthModal } from './AdminReauthModal';
import { AdminScopeLocked } from './AdminScopeLocked';
import { ApiError, isAdminScopeError } from './useMembers';
import {
  useAdminCosmetics,
  useCreateCosmeticItem,
  useUpdateCosmeticItem,
  useArchiveCosmeticItem,
  type CosmeticAdminRow,
  type CosmeticItemInput,
} from './useAdminCosmetics';

type View = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; row: CosmeticAdminRow };

interface FormState {
  kind: CosmeticKind;
  name: string;
  description: string;
  asset_key: string;
  point_cost: string;
  min_age: string;
}

function initialForm(row?: CosmeticAdminRow): FormState {
  return {
    kind: row?.kind ?? 'accessory',
    name: row?.name ?? '',
    description: row?.description ?? '',
    asset_key: row?.asset_key ?? '',
    point_cost: row ? String(row.point_cost) : '',
    min_age: row ? String(row.min_age) : '0',
  };
}

export function CosmeticAdminSection(): ReactElement {
  const { t } = useTranslation();
  const list = useAdminCosmetics();
  const createMut = useCreateCosmeticItem();
  const updateMut = useUpdateCosmeticItem();
  const archiveMut = useArchiveCosmeticItem();

  const [view, setView] = useState<View>({ kind: 'list' });
  const [form, setForm] = useState<FormState>(initialForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<CosmeticAdminRow | null>(null);
  const archiveTitleId = useId();
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({
    open: false,
    retry: null,
  });

  async function runGuarded(
    fn: () => Promise<void>,
    onError: (code: string) => void,
  ): Promise<void> {
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn, onError) });
        return;
      }
      onError(err instanceof ApiError ? err.code : 'unknown');
    }
  }

  function openCreate(): void {
    setForm(initialForm());
    setFormError(null);
    setView({ kind: 'create' });
  }

  function openEdit(row: CosmeticAdminRow): void {
    setForm(initialForm(row));
    setFormError(null);
    setView({ kind: 'edit', row });
  }

  function toInput(): CosmeticItemInput | null {
    const point_cost = Number.parseInt(form.point_cost, 10);
    const min_age = Number.parseInt(form.min_age || '0', 10);
    if (!form.name.trim() || !form.asset_key.trim() || !Number.isInteger(point_cost) || point_cost <= 0) {
      return null;
    }
    return {
      kind: form.kind,
      name: form.name.trim(),
      asset_key: form.asset_key.trim(),
      point_cost,
      min_age: Number.isInteger(min_age) ? min_age : 0,
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
    };
  }

  function submit(): void {
    setFormError(null);
    const input = toInput();
    if (!input) {
      setFormError('invalid');
      return;
    }
    void runGuarded(async () => {
      if (view.kind === 'edit') {
        await updateMut.mutateAsync({ id: view.row.id, input });
      } else {
        await createMut.mutateAsync(input);
      }
      setView({ kind: 'list' });
    }, setFormError);
  }

  function doArchive(row: CosmeticAdminRow): void {
    setActionError(null);
    setConfirmArchive(null);
    void runGuarded(async () => {
      await archiveMut.mutateAsync(row.id);
    }, setActionError);
  }

  function onReauthSuccess(): void {
    const retry = reauth.retry;
    setReauth({ open: false, retry: null });
    retry?.();
  }

  // ── Form view ─────────────────────────────────────────────────────────────
  if (view.kind === 'create' || view.kind === 'edit') {
    const submitting = createMut.isPending || updateMut.isPending;
    return (
      <div className="space-y-4" data-testid="cosmetic-admin-form-view">
        <h3 className="m-0 text-caption font-bold tracking-wider text-ink-2 uppercase">
          {view.kind === 'create'
            ? t('admin.cosmetics.add_title')
            : t('admin.cosmetics.edit_title')}
        </h3>

        <label className="block text-sm">
          <span className="text-ink-2">{t('admin.cosmetics.field.kind')}</span>
          <select
            data-testid="cosmetic-field-kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as CosmeticKind })}
            className="field mt-1"
          >
            {COSMETIC_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`shop.kind.${k}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-ink-2">{t('admin.cosmetics.field.name')}</span>
          <input
            data-testid="cosmetic-field-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="field mt-1"
          />
        </label>

        <label className="block text-sm">
          <span className="text-ink-2">{t('admin.cosmetics.field.assetKey')}</span>
          <input
            data-testid="cosmetic-field-asset"
            value={form.asset_key}
            onChange={(e) => setForm({ ...form, asset_key: e.target.value })}
            placeholder={t('admin.cosmetics.field.assetKeyPlaceholder')}
            className="field mt-1"
          />
        </label>

        <div className="flex gap-3">
          <label className="block flex-1 text-sm">
            <span className="text-ink-2">{t('admin.cosmetics.field.pointCost')}</span>
            <input
              data-testid="cosmetic-field-cost"
              inputMode="numeric"
              value={form.point_cost}
              onChange={(e) => setForm({ ...form, point_cost: e.target.value })}
              className="field mt-1"
            />
          </label>
          <label className="block flex-1 text-sm">
            <span className="text-ink-2">{t('admin.cosmetics.field.minAge')}</span>
            <input
              data-testid="cosmetic-field-minage"
              inputMode="numeric"
              value={form.min_age}
              onChange={(e) => setForm({ ...form, min_age: e.target.value })}
              className="field mt-1"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-ink-2">{t('admin.cosmetics.field.description')}</span>
          <input
            data-testid="cosmetic-field-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="field mt-1"
          />
        </label>

        {formError ? (
          <p className="text-bad text-sm" data-testid="cosmetic-form-error">
            {t('admin.cosmetics.formError')}
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => {
              setFormError(null);
              setView({ kind: 'list' });
            }}
            data-testid="cosmetic-form-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="cosmetic-form-submit">
            {t('admin.cosmetics.save')}
          </Button>
        </div>

        <AdminReauthModal
          open={reauth.open}
          onClose={() => setReauth({ open: false, retry: null })}
          onSuccess={onReauthSuccess}
        />
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="cosmetic-admin-section">
      <div className="flex items-center justify-between">
        <h3 className="m-0 text-caption font-bold tracking-wider text-ink-2 uppercase">
          {t('admin.cosmetics.section_label')}
        </h3>
        <Button onClick={openCreate} data-testid="cosmetic-add">
          {t('admin.cosmetics.add')}
        </Button>
      </div>

      {actionError ? (
        <p className="text-bad text-sm" data-testid="cosmetic-action-error">
          {t('error.offline')}
        </p>
      ) : null}

      {list.isLoading ? (
        <LoadingState lines={3} />
      ) : list.isError && isAdminScopeError(list.error) ? (
        // GET /admin er bak requireAdminScope (D-04): «Prøv igjen» ville bare
        // gitt samme 403, så vi tilbyr å låse opp i stedet.
        <AdminScopeLocked testIdPrefix="cosmetic" message={t('admin.cosmetics.scope_locked')} />
      ) : list.isError ? (
        <ErrorState
          variant="inline"
          heading={t('admin.cosmetics.load_error')}
          onRetry={() => void list.refetch()}
        />
      ) : (list.data ?? []).length === 0 ? (
        <p className="text-ink-2 text-sm" data-testid="cosmetic-empty">
          {t('admin.cosmetics.empty')}
        </p>
      ) : (
        <ul className="divide-card-line divide-y" data-testid="cosmetic-list">
          {(list.data ?? []).map((c) => (
            <li
              key={c.id}
              data-testid={`cosmetic-row-${c.id}`}
              className="flex items-center gap-3 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-ink truncate">{c.name}</p>
                <p className="text-ink-2 text-xs">
                  {t(`shop.kind.${c.kind}`)} ·{' '}
                  {t('admin.cosmetics.pointsBadge', { points: c.point_cost })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => openEdit(c)}
                  data-testid={`cosmetic-edit-${c.id}`}
                >
                  {t('admin.cosmetics.edit')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setConfirmArchive(c)}
                  data-testid={`cosmetic-archive-${c.id}`}
                >
                  {t('admin.cosmetics.archive')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Portal: glasskortet (backdrop-filter) ville ellers vært ramma for
          `fixed`, så dimmingen dekket bare kortet (#159). */}
      {confirmArchive
        ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={archiveTitleId}
          className="bg-dim fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="cosmetic-archive-confirm"
        >
          <div className="bg-sheet w-full max-w-sm space-y-4 rounded-lg p-6 shadow-lg">
            <h2 id={archiveTitleId} className="text-heading-s font-extrabold text-ink">
              {t('admin.cosmetics.archive_confirm_title')}
            </h2>
            <p className="text-ink-2 text-sm">
              {t('admin.cosmetics.archive_confirm_body', { name: confirmArchive.name })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmArchive(null)}
                data-testid="cosmetic-archive-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => doArchive(confirmArchive)}
                data-testid="cosmetic-archive-confirm-btn"
              >
                {t('admin.cosmetics.archive')}
              </Button>
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={onReauthSuccess}
      />
    </div>
  );
}
